import { useState } from 'react';
import { apiService } from '../services/api';
import { LoginResponse, OrgSelectionInfo } from '../types/api.types';

interface OrgSelectorProps {
  sessionToken: string;
  organizations: OrgSelectionInfo[];
  returnUrl?: string;
  onSuccess: (response: LoginResponse) => void;
  onBack: () => void;
}

export default function OrgSelector({
  sessionToken,
  organizations,
  returnUrl,
  onSuccess,
  onBack,
}: OrgSelectorProps) {
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSelect = async (orgCode: string) => {
    setSelectedOrg(orgCode);
    setError('');
    setIsLoading(true);

    try {
      const response = await apiService.selectOrganization({
        session_token: sessionToken,
        organization_code: orgCode,
        return_url: returnUrl,
      });
      // Convert AuthTokenResponse to LoginResponse format
      // Use the NEW session_token from response (backend creates fresh token with mfa_verify step)
      const loginResponse: LoginResponse = {
        session_token: response.session_token || sessionToken,
        access_token: response.access_token,
        token_type: response.token_type,
        user: response.user,
      };
      onSuccess(loginResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to select organization');
      setSelectedOrg(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleBadgeClass = (role: string) => {
    if (role.toLowerCase().includes('admin')) {
      return 'bg-purple-100 text-purple-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Select Organization</h2>
      <p className="text-gray-600 text-sm mb-6">
        You have access to multiple organizations. Please select one to continue.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-2">
        {organizations.map((org) => (
          <button
            key={org.code}
            onClick={() => handleSelect(org.code)}
            disabled={isLoading}
            className={`w-full p-4 text-left rounded border-2 transition-colors ${
              selectedOrg === org.code
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-900">{org.name}</div>
                <div className="text-sm text-gray-500">{org.code}</div>
              </div>
              <span className={`px-2 py-1 text-xs rounded ${getRoleBadgeClass(org.role)}`}>
                {org.role}
              </span>
            </div>
            {selectedOrg === org.code && isLoading && (
              <div className="mt-2 text-sm text-blue-600">Selecting...</div>
            )}
          </button>
        ))}
      </div>

      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={onBack}
          disabled={isLoading}
          className="text-sm text-gray-600 hover:underline disabled:opacity-50"
        >
          Back to login
        </button>
      </div>
    </div>
  );
}
